import type { QueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { browserPreferenceInput } from '@/modules/notifications/browser-preferences'
import { browserSettingsApi } from '@/modules/notifications/browser-settings.api'
import { BrowserNotificationService } from '@/services/browserNotificationService'
import { hashKey, useQueryClient } from '@tanstack/react-query'

import type { BrowserNotificationPreferences } from '@scrambl/shared'
import { SessionChangedError } from '@scrambl/shared/session-scope'

import { queryKeys } from '../query-keys'
import {
  executeNotificationReader,
  isNotificationReaderRefused,
} from './notification-reader-cache'
import { useNotificationPreferenceSession } from './useNotificationPreferenceSession'
import { useNotificationScopedQuery } from './useNotificationScopedQuery'

type Queue = {
  tail: Promise<void>
  pending: number
  uncertain: boolean
  revision: number
  listeners: Set<() => void>
}
// Metadata only, shared by observers of the same owned query. No server bodies.
const queues = new WeakMap<QueryClient, Map<string, Queue>>()
function queueFor(client: QueryClient, identity: string) {
  let entries = queues.get(client)
  if (!entries) {
    entries = new Map()
    queues.set(client, entries)
    const owned = entries
    client.getQueryCache().subscribe(event => {
      if (
        event.type === 'removed' &&
        !owned.get(event.query.queryHash)?.pending
      )
        owned.delete(event.query.queryHash)
    })
  }
  let queue = entries.get(identity)
  if (!queue) {
    queue = {
      tail: Promise.resolve(),
      pending: 0,
      uncertain: false,
      revision: 0,
      listeners: new Set(),
    }
    entries.set(identity, queue)
  }
  return queue
}
function changed(queue: Queue) {
  queue.revision++
  queue.listeners.forEach(listener => listener())
}

/** Serialize partial choices against confirmed readers, never a writer response. */
export function useBrowserNotificationSettings() {
  const session = useNotificationPreferenceSession()
  const client = useQueryClient()
  const prefix = [...queryKeys.notifications.all, 'browser-settings']
  const queryKey = [...prefix, session.key]
  const identity = hashKey(queryKey)
  const queue = queueFor(client, identity)
  useSyncExternalStore(
    listener => {
      queue.listeners.add(listener)
      return () => {
        queue.listeners.delete(listener)
      }
    },
    () => queue.revision,
    () => queue.revision,
  )
  const mounted = useRef(false)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const reader = useNotificationScopedQuery(
    prefix,
    session,
    browserSettingsApi.read,
  )
  const assertCurrent = () => {
    session.assertCurrent()
    if (!mounted.current) throw new SessionChangedError()
  }
  const assertWritable = () => {
    assertCurrent()
    const state = client.getQueryState(queryKey)
    if (
      session.preview ||
      queue.uncertain ||
      state?.data === undefined ||
      state.error ||
      isNotificationReaderRefused(client, queryKey)
    )
      throw new Error('Refresh notification settings before trying again')
  }
  const refreshReader = async () => {
    assertCurrent()
    await client.cancelQueries({ queryKey, exact: true })
    assertCurrent()
    await client.fetchQuery({
      queryKey,
      queryFn: ({ signal }) =>
        executeNotificationReader(
          client,
          queryKey,
          session,
          signal,
          browserSettingsApi.read,
        ),
      staleTime: 0,
      retry: false,
      gcTime: 60000,
    })
    assertCurrent()
  }
  const enqueue = (operation: () => Promise<void>) => {
    assertCurrent()
    queue.pending++
    changed(queue)
    const result = queue.tail.then(operation)
    // A failed predecessor marks uncertainty before the next choice is admitted.
    queue.tail = result
      .catch(() => {})
      .finally(() => {
        queue.pending--
        changed(queue)
        if (
          !queue.pending &&
          !queue.listeners.size &&
          !client.getQueryCache().find({ queryKey, exact: true })
        )
          queues.get(client)?.delete(identity)
      })
    return result
  }
  const update = (
    transform: (
      value: BrowserNotificationPreferences,
    ) => BrowserNotificationPreferences,
  ) => {
    assertWritable()
    return enqueue(async () => {
      assertWritable()
      // Cancel an older reader before dispatch; the follow-up is authoritative.
      await client.cancelQueries({ queryKey, exact: true })
      assertWritable()
      const data =
        client.getQueryData<
          Awaited<ReturnType<typeof browserSettingsApi.read>>
        >(queryKey)
      const baseline =
        data?.preferences ?? BrowserNotificationService.getDefaultPreferences()
      const submitted = browserPreferenceInput.body(
        transform(structuredClone(baseline)),
      )
      try {
        await session.run(new AbortController().signal, context =>
          browserSettingsApi.save(submitted, context),
        )
        assertCurrent()
        await refreshReader()
      } catch (error) {
        queue.uncertain = true
        changed(queue)
        if (error instanceof SessionChangedError) throw error
        throw new Error('Notification settings could not be confirmed')
      }
    })
  }
  const refresh = () =>
    enqueue(async () => {
      await refreshReader()
      queue.uncertain = false
      changed(queue)
    })
  return {
    session,
    identity,
    assertCurrent,
    assertWritable,
    preferences:
      reader.data?.preferences ??
      BrowserNotificationService.getDefaultPreferences(),
    unavailable: !session.enabled || reader.isError || !reader.data,
    canEdit:
      session.enabled &&
      !session.preview &&
      !!reader.data &&
      !reader.isError &&
      !queue.uncertain,
    canRefresh: session.enabled && queue.pending === 0,
    isSaving: queue.pending > 0,
    saveFailed: queue.uncertain,
    update,
    refresh,
  }
}
